import EmptyState from '@/components/common/EmptyState';
import GlassCard from '@/components/GlassCard';
import useThemeColors from '@/hooks/useThemeColors';
import { CertificateSummary } from '@/types';
import { router } from '@inertiajs/react';
import { Award } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    certificates?: CertificateSummary[];
}

const Certificates: React.FC<Props> = ({ certificates }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    const getShareUrl = React.useCallback((sharePath: string) => {
        if (typeof window === 'undefined') {
            return sharePath;
        }

        try {
            return new URL(sharePath, window.location.origin).toString();
        } catch (error) {
            return sharePath;
        }
    }, []);

    const formatDate = (dateString: string | null) => {
        if (!dateString) {
            return t('myAccount.certificatePending');
        }

        const date = new Date(dateString);
        return new Intl.DateTimeFormat(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(date);
    };

    if (certificates === undefined) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
                <svg
                    aria-hidden="true"
                    role="status"
                    className="inline h-10 w-10 animate-spin text-gray-200"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                    ></path>
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentColor"
                    ></path>
                </svg>
                <p className="text-sm text-gray-600">
                    {t('myAccount.loadingCertificates')}
                </p>
            </div>
        );
    }

    if (!certificates.length) {
        return (
            <div className="mx-auto max-w-3xl">
                <EmptyState
                    icon={Award}
                    title={t('myAccount.noCertificatesTitle')}
                    description={t('myAccount.noCertificatesDescription')}
                />
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {t('myAccount.certificatesTitle')}
                    </h2>
                    <p className="text-gray-600 dark:text-neutral-400">
                        {t('myAccount.certificatesSubtitle')}
                    </p>
                </div>
                <div
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${colors.border} ${colors.iconColor}`}
                >
                    <span className="inline-flex h-2 w-2 rounded-full bg-current" />
                    {t('myAccount.totalCertificates', {
                        count: certificates.length,
                    })}
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {certificates.map((certificate) => (
                    <GlassCard
                        key={certificate.signature_id}
                        className="flex h-full flex-col overflow-hidden shadow-lg transition-all hover:border-[var(--color-theme-primary)] hover:shadow-xl"
                    >
                        <div className="relative h-52 w-full bg-white/30 dark:bg-white/5">
                            {certificate.image_url ? (
                                <img
                                    src={certificate.image_url}
                                    alt={t('myAccount.certificatePreviewAlt')}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full items-center justify-center text-gray-400">
                                    <span className="text-sm font-medium">
                                        {t(
                                            'myAccount.certificatePreviewPlaceholder'
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-1 flex-col justify-between gap-6 p-6">
                            <div className="space-y-3">
                                <div className="inline-flex items-center gap-2 rounded-full bg-white/40 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-white/10 dark:text-neutral-300">
                                    <span className="inline-flex h-2 w-2 rounded-full bg-current text-transparent" />
                                    {t('myAccount.certificateIssuedOn', {
                                        date: formatDate(certificate.issued_at),
                                    })}
                                </div>
                                <p className="text-sm text-gray-500 dark:text-neutral-400">
                                    {t('myAccount.certificateSignature', {
                                        id: certificate.signature_id
                                            .slice(0, 8)
                                            .toUpperCase(),
                                    })}
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <button
                                    type="button"
                                    onClick={() =>
                                        router.visit(
                                            `/my-account/certificates/${certificate.signature_id}/`
                                        )
                                    }
                                    className={`btn-gradient inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold focus:ring-2 focus:ring-offset-2 focus:outline-none ${colors.buttonPrimaryFocus}`}
                                >
                                    {t('myAccount.viewCertificate')}
                                </button>
                                <a
                                    href={getShareUrl(certificate.share_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`inline-flex items-center justify-center rounded-full border px-5 py-2.5 text-sm font-semibold transition focus:ring-2 focus:ring-offset-2 focus:outline-none ${colors.border} ${colors.buttonSecondary} ${colors.buttonPrimaryFocus}`}
                                >
                                    {t('myAccount.shareCertificate')}
                                </a>
                            </div>
                        </div>
                    </GlassCard>
                ))}
            </div>
        </div>
    );
};

export default Certificates;
